import { useContext, useState } from 'react';
import {
  Button,
  ButtonSize,
  ButtonWidth,
  Drawer,
  useNextgenRouter,
  useSession,
  useTranslation,
} from '@nextgen-web-framework/all';
import {
  CartRoutePaths,
  CreateListRequest,
  ErrorAPI,
  ListExceptionDetail,
  ListResponse,
  UpdateListRequest,
  UpdateListResponse,
  getWishlist,
  myListApi,
  updateMyList,
} from '@commerce-cart-webcomponents/sdk';
import { useDispatch } from 'react-redux';
import { CreateList } from '../create-list/CreateList';
import { useCartNotification } from '../../../../common/hooks/useCartNotification';
import { TOAST_TIMEOUT } from '../../../../common/types/enums';
import { CartNotificationTheme } from '../../../../common/components/cart-notification/types/enums';
import { ButtonLoader } from '../../../../common/components';
import { AllCustomListContext, useExceptionToast } from '../../../../common';
import { type CreateListDrawerProps } from './types/propsType';

export const CreateListDrawer = ({
  listDetails,
  isEdit,
  onDrawerClose,
  onSuccessAction,
  showLoaderCB,
}: CreateListDrawerProps) => {
  const t = useTranslation();
  const dispatch = useDispatch();
  const { isABO, isMember } = useSession();
  const router = useNextgenRouter();
  const successMessage = !isEdit
    ? t('cart.createListDrawer.listCreateSuccess')
    : t('cart.createListDrawer.listUpdateSuccess');
  const triggerToast = useExceptionToast();
  const { updateCustomList } = useContext(AllCustomListContext);
  const { createToast, deleteToast } = useCartNotification(isEdit ? 'editList' : 'createList');
  const [listData, setListData] = useState<CreateListRequest | UpdateListRequest>(
    (listDetails?.payLoad as CreateListRequest) ?? {
      entries: [],
      name: '',
      description: '',
      category: '',
    },
  );
  const [showLoader, setShowLoader] = useState(false);

  const pleaseDeleteToCreateNew = t('cart.createList.pleaseDeleteToCreateNew');
  const maxLimitReached = t('cart.createList.maxLimitReached');

  const successHandler = (res?: ListResponse) => {
    if (res) {
      createToast(
        {
          theme: CartNotificationTheme.SUCCESS,
          rightSeperator: false,
          content: successMessage,
          clickIcon: 'times',
          icon: 'check-circle',
          timeOut: TOAST_TIMEOUT,
        },
        deleteToast,
      );
      onSuccessAction?.(res);
      if (!isEdit) {
        router.push(`${CartRoutePaths.LIST}/${res?.listId}`);
      }
    }
  };

  const onConfirmButtonClick = () => {
    setShowLoader(true);
    if (!isEdit) {
      myListApi
        .createList(listData as CreateListRequest)
        .then(successHandler)
        .catch((...errorProps: ErrorAPI[]) => {
          if (errorProps[0].responseBody.detail === ListExceptionDetail.ListMaxLimitExceeded) {
            createToast(
              {
                theme: CartNotificationTheme.RED,
                rightSeperator: false,
                content: maxLimitReached,
                subtext: pleaseDeleteToCreateNew,
                clickIcon: 'times',
                icon: 'exclamation-square',
                timeOut: TOAST_TIMEOUT,
              },
              deleteToast,
            );
            onDrawerClose?.();
          } else {
            triggerToast(errorProps);
          }
        })
        .finally(() => {
          setShowLoader(false);
        });
    } else if (listDetails?.listId) {
      showLoaderCB?.(true);
      myListApi
        .updateList([
          {
            ...listData,
            listId: listDetails.listId,
            version: listDetails.version ?? 0,
          },
        ])
        .then((res: UpdateListResponse) => {
          dispatch(updateMyList(getWishlist(res) as ListResponse));
          updateCustomList(res?.lists?.[0] || {});
          successHandler(res.lists?.[0]);
        })
        .catch((...errorProps: ErrorAPI[]) => triggerToast(errorProps))
        .finally(() => {
          showLoaderCB?.(false);
          setShowLoader(false);
        });
    }
  };

  const enableConfirmCTA = () =>
    Number(listData?.name?.length) > 0 &&
    (listData.description as string).length > 0 &&
    (isABO() || isMember() ? (listData.category as string).length > 0 : true);

  return (
    <Drawer
      footer={
        showLoader ? (
          <ButtonLoader size={ButtonSize.MEDIUM} />
        ) : (
          <Button
            disabled={!enableConfirmCTA()}
            title={t('cart.addToListdrawer.confirm')}
            buttonWidth={ButtonWidth.FULL_WIDTH}
            data-testId="confirm-button"
            onClick={onConfirmButtonClick}
          />
        )
      }
      header={isEdit ? t('cart.createListDrawer.editListDetails') : t('cart.createListDrawer.createList')}
      closeIcon={true}
      closeOnBackdropClick={true}
      bottomGap={0}
      onClose={onDrawerClose}
    >
      <CreateList listDetails={listDetails?.payLoad} onListDataChange={(data) => setListData(data)} />
    </Drawer>
  );
};
